import React, { Fragment } from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import pt from 'prop-types';

import { CONTENT_MODEL_VIEWS, APP_ACTION_HINT_DELAY } from 'constants/content';

import {
  classNames,
  evalConditionsWithAppFields,
  locationWithQuery,
  objectToArray,
} from 'utils';
import { getVisibleConfigsByLocation } from 'utils/collection';
import { canRunAppContentActions } from 'utils/app';
import { storefrontCollectionLink } from 'utils/storefront';
import { collectionFromUrlParam } from 'utils/content';
import { openFrontendAccessUrl } from 'utils/app-frontend';
import { expandString } from 'utils/string';

import Link from 'components/link';
import Icon from 'components/icon';
import Tabs from 'components/tabs';
import Tooltip from 'components/tooltip';
import BackButton from 'components/button/back';
import LinkButton from 'components/button/link';
import DropdownButton from 'components/button/dropdown';
import ConsoleLink from 'components/console/link';
import ModelLink from 'components/model/link';
import { FadeLoading, FadeOut } from 'components/transitions';
import LocaleSelector from 'components/locale/selector';
import ViewSelector from 'components/content/view-selector';
import HelpLink from 'components/help-link';
import AppIcon from 'components/apps/icon';
import {
  AppActionRunGuard,
  AppActionRunsRestore,
} from 'components/apps/action-run';

import AppIndicator from './app-indicator';

import './view.scss';
import './print.scss';

function processActionLink(action, record) {
  if (typeof action.link !== 'string') {
    return action;
  }

  const processed = { ...action };

  if (processed.link.includes('{') && record) {
    processed.link = expandString(processed.link, record);
  }

  if (typeof processed.target === 'string') {
    if (processed.target === 'self') {
      delete processed.target;
    } else if (processed.target[0] !== '_') {
      processed.target = '_' + processed.target;
    }
  }

  if (processed.internal && processed.target === '_blank' && processed.app_id) {
    const { app_id: appId, link } = processed;

    processed.onClick = (event) => {
      event.preventDefault();
      openFrontendAccessUrl(appId, link);
    };
  }

  if (typeof processed.external === 'string' && !processed.target) {
    processed.link += `?_external_link=${encodeURIComponent(
      record ? expandString(processed.external, record) : processed.external,
    )}`;
  }

  return processed;
}

// An app action's conditions read the record, and the app's saved settings as
// $settings
function actionConditionValues(record, settings, appId) {
  const $settings = appId && settings?.[appId];

  return { ...record, ...($settings ? { $settings } : undefined) };
}

export default class View extends React.PureComponent {
  static propTypes = {
    uri: pt.string,
    detail: pt.bool,
    record: pt.object,
    edited: pt.bool,
    sectionTitle: pt.string,
    headerTitle: pt.node,
    headerStatus: pt.node,
    headerActions: pt.array,
    headerComponent: pt.object,
    menuActions: pt.array,
    extraActions: pt.array,
    footerActions: pt.array,
    fields: pt.array,
    form: pt.object,
    tags: pt.array,
    editableTags: pt.array,
    tabs: pt.oneOfType([pt.array, pt.object]),
    banner: pt.node,
    defaultTabs: pt.array,
    router: pt.object,
    location: pt.object,
    devtools: pt.object,
    localized: pt.bool,
    withPreferences: pt.bool,
    children: pt.node,

    onSubmitAddTag: pt.func,
    onClickRemoveTag: pt.func,
    onAppActionComplete: pt.func,
  };

  static contextTypes = {
    submit: pt.func,
    location: pt.object,
    getLastUrl: pt.func,
    setLastUrl: pt.func,
    toggleLocale: pt.func,
    router: pt.object,
    client: pt.object,
    settings: pt.object,
    user: pt.object,
    isAdvancedUserPermissions: pt.bool,
    openModal: pt.func,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      tabs: getVisibleConfigsByLocation(props.tabs, props.location),
      defaultTabs: getVisibleConfigsByLocation(
        props.defaultTabs,
        props.location,
      ),
      ...this.getActions(props, context),
    };
  }

  componentDidUpdate(prevProps) {
    const {
      record,
      edited,
      headerActions,
      footerActions,
      menuActions,
      extraActions,
    } = this.props;

    if (
      prevProps.headerActions !== headerActions ||
      prevProps.footerActions !== footerActions ||
      prevProps.menuActions !== menuActions ||
      prevProps.extraActions !== extraActions ||
      prevProps.record !== record ||
      prevProps.edited !== edited
    ) {
      this.setState({
        ...this.getActions(this.props, this.context),
      });
    }
  }

  static getDerivedStateFromProps(props, state) {
    let obj = null;

    const changedLocation = props.location !== state.location;

    if (changedLocation) {
      obj = Object.assign(obj ?? {}, {
        location: props.location,
      });
    }

    if (changedLocation || props.fields !== state.prevFields) {
      obj = Object.assign(obj ?? {}, {
        prevFields: props.fields,
        tabs: getVisibleConfigsByLocation(props.tabs, props.location),
        defaultTabs: getVisibleConfigsByLocation(
          props.defaultTabs,
          props.location,
        ),
      });
    }

    return obj;
  }

  componentWillUnmount() {
    if (this.wentBack) {
      this.context.setLastUrl('');
    } else if (this.context.location.pathname.split('/').length > 2) {
      this.context.setLastUrl(this.context.location.pathname);
    }
  }

  onClickSubmit = (event) => {
    event.preventDefault();
    if (this.props.form && this.props.form.submit) {
      this.props.form.submit();
    } else if (this.context.submit) {
      this.context.submit();
    }
  };

  onClickAppAction(action, event) {
    event.preventDefault();
    const { record, onAppActionComplete } = this.props;

    this.context.openModal('AppAction', {
      app: action.app,
      action,
      context: {
        ...action.context,
        ...(record?.id && { record_id: record.id }),
      },
      onComplete: onAppActionComplete,
    });
  }

  renderTags() {
    const { tags, editableTags, onClickRemoveTag, onSubmitAddTag } = this.props;

    const tagsFiltered = [];

    if (Array.isArray(tags)) {
      for (const tag of tags) {
        if (tag) {
          tagsFiltered.push(<li key={tag}>{tag}</li>);
        }
      }
    }

    if (Array.isArray(editableTags)) {
      for (const tag of editableTags) {
        tagsFiltered.push(
          <li key={tag} className="editable">
            {tag}
            <a
              href=""
              data-tag={tag}
              className="remove"
              onClick={onClickRemoveTag}
            >
              &times;
            </a>
          </li>,
        );
      }
    }

    if (tagsFiltered.length <= 0) {
      return;
    }

    return (
      <ul className="view-body-tags">
        {tagsFiltered}
        {onSubmitAddTag && (
          <li className="add">
            <a href="" onClick={this.onClickAddTag}>
              +
            </a>
          </li>
        )}
      </ul>
    );
  }

  getBackUrl() {
    const { nested, backUrl } = this.props;

    if (backUrl) {
      return backUrl;
    }

    const lastUrl = this.context.getLastUrl();

    if (
      !nested &&
      lastUrl &&
      !lastUrl.includes(this.context.location.pathname)
    ) {
      return lastUrl;
    }
  }

  onClickBack = (event) => {
    const { router } = this.context;
    const { backUrl } = this.props;

    this.wentBack = true;

    if (backUrl) {
      event.preventDefault();
      router.replace(backUrl);
    } else if (this.getBackUrl()) {
      event.preventDefault();
      router.goBack();
    }
  };

  renderDevtoolsConsoleLink(props, context) {
    const { devtools } = props;
    if (!devtools || !context.user.devtools || devtools.viewConsole === false) {
      return null;
    }
    return (
      <ConsoleLink
        model={collectionFromUrlParam(devtools.model)}
        uri={devtools.uri || (devtools.record && devtools.record.id)}
        buttonType={null}
        icon={false}
        label="View in console"
      />
    );
  }

  renderDevtoolsModelLink(props, context) {
    const { devtools } = props;
    if (
      !devtools ||
      !context.user.devtools ||
      devtools.editModel === false ||
      devtools.model[0] === ':'
    ) {
      return null;
    }
    return (
      <ModelLink
        model={devtools.editModel || devtools.model}
        zone={devtools.zone}
        buttonType={null}
        icon={false}
      />
    );
  }

  renderStorefrontLink(props) {
    const { storefront } = props;

    if (
      !storefront ||
      !storefront.collection ||
      !storefront.record ||
      !storefront.storefronts?.primary
    ) {
      return null;
    }

    const {
      collection,
      record,
      storefronts: { primary },
    } = storefront;

    const storefrontLink = storefrontCollectionLink(
      collection,
      record,
      primary,
    );

    return (
      <a
        key="1"
        href={storefrontLink}
        onClick={(event) => {
          event.preventDefault();
          window.open(storefrontLink, '_blank');
        }}
      >
        View in storefront
      </a>
    );
  }

  renderLocaleSelector() {
    if (!this.props.localized) {
      return null;
    }
    return {
      key: 'header-locale-selector',
      component: <LocaleSelector />,
    };
  }

  renderViewSelector() {
    const { views } = this.props;
    // Disabled for now
    if (true || !views?.length) {
      return null;
    }
    return {
      key: 'header-view-selector',
      component: <ViewSelector views={views} />,
    };
  }

  renderStatus() {
    const { headerStatus } = this.props;
    if (!headerStatus) {
      return null;
    }
    return {
      key: 'header-status',
      component: <div className="view-header-status">{headerStatus}</div>,
    };
  }

  renderAppActionRunsRestore() {
    const { record, onAppActionComplete } = this.props;
    const { appActions } = this.state;

    if (!appActions.length) {
      return null;
    }

    return {
      key: 'app-action-runs-restore',
      showAlways: true,
      component: (
        <AppActionRunsRestore
          actions={appActions}
          recordId={record?.id}
          onComplete={onAppActionComplete}
        />
      ),
    };
  }

  getActionComponent = (action, index, array, linkProps) => {
    if (!action) {
      return null;
    }

    if (React.isValidElement(action)) {
      return action;
    }

    if (action.function) {
      return (
        <AppActionRunGuard
          key={index}
          action={action}
          recordId={this.props.record?.id}
        >
          {(guarded) => this.renderActionItem(guarded, linkProps)}
        </AppActionRunGuard>
      );
    }

    return (
      <Fragment key={index}>
        {this.renderActionItem(action, linkProps)}
      </Fragment>
    );
  };

  renderActionItem(action, linkProps) {
    return (
      action.component || (
        <Link
          to={action.link}
          onClick={action.disabled ? undefined : action.onClick}
          type={action.type}
          disabled={action.disabled}
          className={action.className}
          {...linkProps}
        >
          {/* Beside the menu, so the hint doesn't cover the other items. The
              offset clears the item and menu padding and the menu border */}
          <Tooltip
            message={action.disabled ? action.help : action.hint}
            enterDuration={action.disabled ? undefined : APP_ACTION_HINT_DELAY}
            side="left"
            sideOffset={17}
          >
            {action.icon && <Icon type={action.icon} />}
            {action.function ? (
              <span className="with-icon">
                <AppIcon app={action.app} size={16} />
                {action.label}
              </span>
            ) : (
              action.label
            )}
          </Tooltip>
        </Link>
      )
    );
  }

  getVisibleActions(props) {
    const {
      record,
      headerActions: origHeaderActions = [],
      footerActions: origFooterActions = [],
      menuActions: origMenuActions = [],
      extraActions: origExtraActions = [],
    } = props;
    const { client, settings } = this.context;

    const arrays = [
      origHeaderActions,
      origFooterActions,
      origMenuActions,
      origExtraActions,
    ].map((actions) =>
      actions.filter((act) => {
        if (!act || act.hidden) return false;
        if (act.conditions) {
          return evalConditionsWithAppFields(
            client,
            '',
            act.conditions,
            actionConditionValues(record, settings, act.app?.id ?? act.app_id),
          );
        }
        return true;
      }),
    );

    for (const actions of arrays) {
      for (let i = 0; i < actions.length; ++i) {
        const action = actions[i];

        actions[i] = action.function
          ? { ...action, onClick: this.onClickAppAction.bind(this, action) }
          : processActionLink(action, record);
      }
    }

    return arrays;
  }

  getContentModelActions(props) {
    const {
      record,
      devtools,
      edited,
      headerActions = [],
      extraActions = [],
      onAppActionComplete,
    } = props;
    const { client, settings, user, isAdvancedUserPermissions } = this.context;
    const model = devtools?.model;
    if (!model) return [];

    const canRunAppActions = canRunAppContentActions(
      user,
      isAdvancedUserPermissions,
      model,
    );

    const views = CONTENT_MODEL_VIEWS[model];
    if (!views?.length) return [];

    // Skip actions already present in header or extra actions (avoids duplicates on generic pages)
    const existingActionIds = new Set(
      [...headerActions, ...extraActions].filter((a) => a?.id).map((a) => a.id),
    );

    const customActions = [];
    for (const view of views) {
      if (view.type !== 'record') continue;

      // App fields on standard models are stored under $app, so conditions
      // name them without a prefix, as in the app's field conditions
      const rootPath =
        view.app_id && !view.collectionModel?.app_id
          ? `$app.${view.app?.slug_id || view.app_id}`
          : '';

      for (const action of [
        ...(view.actions || []),
        ...(view.extraActions || []),
      ]) {
        if (action.submit || action.delete || action.hidden) continue;
        if (!action.link && !action.function) continue;
        // App functions run against a saved record, on pages that refetch
        // it after a run, for users who may run them. App-owned records show
        // the functions of their own view only.
        if (
          action.function &&
          (!record?.id ||
            !onAppActionComplete ||
            !canRunAppActions ||
            view.collectionModel?.app_id)
        ) {
          continue;
        }
        if (existingActionIds.has(action.id)) continue;
        if (
          action.conditions &&
          !evalConditionsWithAppFields(
            client,
            rootPath,
            action.conditions,
            actionConditionValues(record, settings, view.app_id),
          )
        ) {
          continue;
        }
        customActions.push(action);
      }
    }

    if (!customActions.length) return [];

    return customActions.map((action) =>
      action.function
        ? {
            ...action,
            ...(edited && {
              disabled: true,
              help: 'Save your changes before running this action',
            }),
            onClick: this.onClickAppAction.bind(this, action),
          }
        : processActionLink(action, record),
    );
  }

  getActions(props, context) {
    let [headerActions, footerActions, menuActions, extraActions] =
      this.getVisibleActions(props);

    const storefrontLink = this.renderStorefrontLink(props);
    const modelLink = this.renderDevtoolsModelLink(props, context);
    const consoleLink = this.renderDevtoolsConsoleLink(props, context);

    const devtoolItems = [];

    if (storefrontLink) {
      devtoolItems.push(storefrontLink);
    }

    if (consoleLink) {
      devtoolItems.push(consoleLink);
    }

    if (modelLink) {
      devtoolItems.push(modelLink);
    }

    const contentModelActions = this.getContentModelActions(props);

    // App function actions, to find their runs active before the page loaded
    const appActions = [
      ...headerActions,
      ...menuActions,
      ...extraActions,
      ...contentModelActions,
    ].filter((action) => action.function);

    const extraItems = menuActions.map(this.getActionComponent);

    if (
      menuActions.length > 0 &&
      (contentModelActions.length > 0 ||
        devtoolItems.length > 0 ||
        extraActions.length > 0)
    ) {
      extraItems.push(<hr />);
    }

    extraItems.push(...contentModelActions.map(this.getActionComponent));

    if (
      contentModelActions.length > 0 &&
      (devtoolItems.length > 0 || extraActions.length > 0)
    ) {
      extraItems.push(<hr />);
    }

    extraItems.push(...devtoolItems);

    if (devtoolItems.length > 0 && extraActions.length > 0) {
      extraItems.push(<hr />);
    }

    extraItems.push(...extraActions.map(this.getActionComponent));

    // Append devtools and extra actions to header action menu append extra menu
    const actionMenu = headerActions.find((act) => Boolean(act.isActionMenu));
    const actionItems = actionMenu?.component?.props?.items;

    if (actionItems) {
      if (actionItems.length > 0) {
        actionItems.push(<hr />);
      }

      for (const item of extraItems) {
        actionItems.push(item);
      }
    } else if (extraItems.length > 0) {
      const subActions = headerActions.filter(
        (act) => (act.type || act.component?.props?.type) === 'sub',
      );

      headerActions = [
        ...subActions,
        {
          // Show the menu on an empty list when any of its items would be
          showAlways: [
            ...menuActions,
            ...contentModelActions,
            ...extraActions,
          ].some((act) => act?.showAlways),
          component: (
            <DropdownButton anchor="right" type="secondary" items={extraItems}>
              Actions
            </DropdownButton>
          ),
        },
        ...headerActions.filter((act) => !subActions.includes(act)),
      ];
    }

    return { headerActions, footerActions, appActions };
  }

  getEnhancedTabs(tabs) {
    const {
      client: { appsById },
    } = this.context;
    // Always convert objects to arrays
    let enhancedTabs = cloneDeep(tabs);
    if (!isEmpty(tabs) && !Array.isArray(tabs)) {
      enhancedTabs = tabs.map((val, id) => ({ id, ...val }));
    }

    // Add app logos and click actions to tabs
    for (const tab of enhancedTabs) {
      // TODO: Add click handler for tab actions
      // tab.onClick = this.onClickTab();

      // Tabs shows the app icon before the label
      const app = appsById?.[tab.app_id];
      if (app) {
        tab.app = app;
      }
    }

    return enhancedTabs;
  }

  onClickTab = (tab) => {
    const { location, router } = this.props;
    if (location.query.tab !== tab) {
      router.push(
        locationWithQuery(location, {
          tab: tab === 'default' ? undefined : tab,
        }),
      );
    }

    // TODO: show options when clicked while already on tab
  };

  renderTabs() {
    const { uri, tabs: tabsIn, withPreferences } = this.props;
    const { location, router } = this.context;

    const tabs = tabsIn?.length > 0 ? tabsIn : objectToArray(tabsIn);

    if (!tabs?.length || !location) return null;

    return (
      <div className="view-header-tabs">
        <Tabs
          uri={uri}
          location={location}
          router={router}
          items={this.getEnhancedTabs(tabs)}
          active={location.query.tab}
          withPreferences={withPreferences}
          overflow={true}
        />
      </div>
    );
  }

  renderHeaderAction(action, index, isChild) {
    const linkButton = (
      <LinkButton
        key={action.key || index}
        to={action.link}
        target={action.target}
        onClick={action.submit ? this.onClickSubmit : action.onClick}
        type={action.type}
        className={action.className}
        disabled={action.disabled || action.type === 'disabled'}
        size={action.size || isChild ? 'sm' : undefined}
      >
        {(action.fa || action.icon) && (
          <Icon fa={action.fa} type={action.icon} />
        )}
        {action.label}
      </LinkButton>
    );

    // Titles and disabled help show right away, hints after a delay
    const tooltip = action.title || (action.disabled && action.help);
    const hint = !tooltip && action.hint;

    return tooltip || hint ? (
      <Tooltip
        key={action.key || index}
        message={tooltip || hint}
        enterDuration={hint ? APP_ACTION_HINT_DELAY : undefined}
        side="bottom"
        align="end"
      >
        {linkButton}
      </Tooltip>
    ) : (
      linkButton
    );
  }

  render() {
    const {
      uri,
      backTitle,
      sectionTitle,
      headerTitle,
      headerSubtitle,
      headerImage,
      headerComponent,
      banner,
      className,
      collectionEmpty,
      submitting,
      app,
      childId,
      showApp = true,
      detail = false,
      smaller = false,
      wide = false,
      full = false,
      helpLink = true,
      overrideHelpLinkKey = null,
      record,
    } = this.props;

    const { headerActions, footerActions } = this.state;
    const isChild = Boolean(childId);

    const backUrl = this.getBackUrl();
    const hasCrumb = Boolean(backTitle || sectionTitle);

    return (
      <div className={classNames('view-container', className)}>
        {/* Move header actions to the top to apply CSS `position: sticky;` */}
        <div
          className={classNames('view-header-actions', {
            'with-title': Boolean(sectionTitle),
            'with-crumb': hasCrumb,
            smaller,
            detail,
            wide,
            full,
          })}
        >
          <div className="view-header-actions-buttons">
            {[
              !isChild && this.renderStatus(),
              !isChild && this.renderAppActionRunsRestore(),
              !isChild && this.renderViewSelector(),
              !isChild && this.renderLocaleSelector(),
              ...headerActions,
            ].map((action, index) => {
              if (!action || (collectionEmpty && !action.showAlways)) {
                return null;
              }

              if (action.component) {
                return (
                  <Fragment key={action.key || index}>
                    {action.component}
                  </Fragment>
                );
              }

              if (action.function) {
                return (
                  <AppActionRunGuard
                    key={action.key || index}
                    action={action}
                    recordId={record?.id}
                  >
                    {(guarded) =>
                      this.renderHeaderAction(guarded, index, isChild)
                    }
                  </AppActionRunGuard>
                );
              }

              return this.renderHeaderAction(action, index, isChild);
            })}
          </div>
        </div>

        <header
          className={classNames('view-header', {
            'with-crumb': hasCrumb,
            smaller,
            detail,
            wide,
            full,
          })}
        >
          <div className="view-header-title">
            {hasCrumb && (
              <span className="view-header-crumb">
                {detail ? (
                  <BackButton to={backUrl || uri} onClick={this.onClickBack}>
                    {backTitle || (backUrl ? 'Back' : sectionTitle)}
                  </BackButton>
                ) : (
                  <Link to={backUrl || uri} onClick={this.onClickBack}>
                    {backTitle || sectionTitle}
                  </Link>
                )}
              </span>
            )}

            {headerComponent
              ? headerComponent
              : headerTitle &&
                (headerImage ? (
                  <div className="with-image">
                    <div className="view-header-image">{headerImage}</div>

                    <h3
                      className={classNames('view-header-name', {
                        'with-crumb': Boolean(sectionTitle),
                      })}
                    >
                      {headerTitle}
                    </h3>

                    {headerSubtitle && (
                      <div className="view-header-note">{headerSubtitle}</div>
                    )}
                  </div>
                ) : (
                  <h3
                    className={classNames('view-header-name', {
                      'with-crumb': Boolean(sectionTitle),
                    })}
                  >
                    {headerTitle}
                  </h3>
                ))}
          </div>

          {headerSubtitle && !headerImage && (
            <div className="view-header-subtitle">{headerSubtitle}</div>
          )}

          {this.renderTags()}

          {banner && <div className="view-header-banner">{banner}</div>}

          {this.renderTabs()}
        </header>

        <div
          className={classNames('view-body', {
            detail,
            smaller,
            wide,
            full,
          })}
        >
          {this.props.children}

          {app && showApp !== false && <AppIndicator app={app} />}
        </div>

        <FadeOut duration={100} className="view-loading-mask" />

        <FadeLoading active={submitting} className="view-loading-mask" />

        {footerActions.length > 0 && (
          <footer className={classNames('view-footer', { detail, smaller })}>
            <div className="view-footer-buttons">
              {footerActions.map((action, index) =>
                action.component ? (
                  <Fragment key={action.key || index}>
                    {action.component}
                  </Fragment>
                ) : (
                  <LinkButton
                    key={action.key || index}
                    to={action.link}
                    target={action.target}
                    onClick={action.onClick}
                    type={action.type || 'secondary'}
                    disabled={action.disabled || action.type === 'disabled'}
                    size={action.size || 'sm'}
                  >
                    {(action.fa || action.icon) && (
                      <Icon fa={action.fa} type={action.icon} />
                    )}

                    {action.label}
                  </LinkButton>
                ),
              )}
            </div>
          </footer>
        )}

        {helpLink && (
          <div className="view-help-link">
            <HelpLink overrideKey={overrideHelpLinkKey} />
          </div>
        )}
      </div>
    );
  }
}
